import {
    AppBar,
    Avatar,
    Badge,
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Stack,
    TableCell,
    TableRow,
    Toolbar,
    Typography,
    useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useUserContext } from '../../App';
import { useEffect } from 'react';
import { useState } from 'react';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { formatDate } from '../../utilities/misc';
import Icon from './Icon';
import BreadCrumb from './BreadCrumb';
import { Logout, PersonAdd, Settings } from '@mui/icons-material';
import CustomTable from '../ui/CustomTable';

const Navbar = ({ drawerWidth, toggle, toggleMiniDrawer, miniDrawer, interOrderInfo }) => {
    const { setUser } = useUserContext();

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const navigate = useNavigate();
    const logOut = () => {
        localStorage.removeItem('token');
        setUser(null);
        navigate('/');
    };
    const theme = useTheme();

    const [liveTime, setLiveTime] = useState(formatDate());

    useEffect(() => {
        const liveTimeInterval = setInterval(() => setLiveTime(formatDate()), 30000);
        return () => {
            clearInterval(liveTimeInterval);
        };
    }, []);

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                width: { sm: `calc(100% - ${drawerWidth}px)` },
                ml: { sm: `${drawerWidth}px` },
                bgcolor: 'transparent',
                color: theme.palette.text.primary,
                py: 1,
                px: { xs: 1, sm: 5 },
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid rgba(0,0,0,0.1)',
            }}>
            <Toolbar>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                    <Stack direction="row" alignItems="center">
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={toggleMiniDrawer}
                            size="small"
                            sx={{
                                mr: 1,
                                display: { xs: 'none', sm: 'flex' },
                                backgroundColor: '#1F1F1F',
                                borderRadius: 2,
                                border: '1px solid rgba(255,255,255,0.1)',
                            }}>
                            {miniDrawer ? <ChevronRightRoundedIcon /> : <ChevronLeftRoundedIcon />}
                        </IconButton>
                        <IconButton
                            color="inherit"
                            edge="start"
                            onClick={toggle}
                            size="small"
                            sx={{
                                mr: 1,
                                display: { xs: 'flex', sm: 'none' },
                                backgroundColor: '#1F1F1F',
                                borderRadius: 2,
                                border: '1px solid rgba(255,255,255,0.1)',
                            }}>
                            <ChevronRightRoundedIcon />
                        </IconButton>
                        <Box sx={{ mr: 1, mt: 1, display: { sm: 'none' } }}>
                            <img
                                src={`/assets/images/logo.svg`}
                                alt="logo"
                                style={{ margin: '0 auto', width: '5rem' }}
                            />
                        </Box>
                        <BreadCrumb />
                    </Stack>

                    <Stack
                        sx={{ display: { xs: 'flex', sm: 'none' } }}
                        direction="row"
                        spacing={1}
                        alignItems="stretch">
                        <IconButton
                            sx={{
                                backgroundColor: '#1F1F1F',
                                borderRadius: `${theme.shape.borderRadius}px !important`,
                                border: '1px solid rgba(255,0,0,0.3)',
                            }}
                            onClick={logOut}>
                            <Icon path="bulk/login" />
                        </IconButton>
                    </Stack>

                    <Stack
                        sx={{ display: { xs: 'none', sm: 'flex' } }}
                        direction="row"
                        spacing={2}
                        alignItems="stretch">
                        <Badge badgeContent={interOrderInfo?.total} color="primary">
                            <IconButton
                                onClick={handleClick}
                                size="small"
                                aria-controls={open ? 'account-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                                sx={{
                                    backgroundColor: `${interOrderInfo?.total > 0 ? '#ad9508' : '#1F1F1F'}`,
                                    borderRadius: `${theme.shape.borderRadius}px !important`,
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    gap: 0.5,
                                    px: 1.8,
                                }}>
                                <NotificationsActiveIcon />
                            </IconButton>
                        </Badge>
                        <IconButton
                            sx={{
                                backgroundColor: '#1F1F1F',
                                borderRadius: `${theme.shape.borderRadius}px !important`,
                                border: '1px solid rgba(255,255,255,0.1)',
                                gap: 0.5,
                                px: 1.8,
                            }}>
                            <Icon path="bulk/calendar" />
                            <Typography variant="body2">{liveTime}</Typography>
                        </IconButton>
                        <Stack direction="row" spacing={2} alignItems="stretch">
                            <IconButton
                                onClick={() => navigate('/profile')}
                                sx={{
                                    backgroundColor: '#1F1F1F',
                                    borderRadius: `${theme.shape.borderRadius}px !important`,
                                    border: '1px solid',
                                    borderColor: 'secondary.main',
                                    gap: 0.5,
                                    px: 1.8,
                                }}>
                                <Icon path="bulk/user" />
                                <Typography variant="body2">Admin</Typography>
                            </IconButton>

                            <IconButton
                                onClick={logOut}
                                sx={{
                                    backgroundColor: '#1F1F1F',
                                    borderRadius: `${theme.shape.borderRadius}px !important`,
                                    border: '1px solid rgba(255,0,0,0.3)',
                                }}>
                                <Icon path="bulk/login" />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Stack>
            </Toolbar>
            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                        '&::before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
                <CustomTable title="International Orders" fields={['USER', 'TYPE', 'AMOUNT', 'CREADTED AT']}>
                    {interOrderInfo?.orders?.map((label) => (
                        <TableRow>
                            <TableCell>{label?.user?.username}</TableCell>
                            <TableCell>{label?.labelType?.name}</TableCell>
                            <TableCell>${label?.price}</TableCell>
                            <TableCell>{label?.createdAt}</TableCell>
                            <TableCell>{label?.allTimeOrdersCount}</TableCell>
                        </TableRow>
                    ))}
                </CustomTable>
            </Menu>
        </AppBar>
    );
};

export default Navbar;
